// Phase 2 damper-edit sheet.
//
// Engineer picks ANY subset of the 4 corners and edits damper_code +
// clicks. Submission builds a SPARSE overlay (only edited fields go in
// the payload) and POSTs to /api/tests/{id}/runs. The backend's strict
// DamperSettingPayload validator rejects malformed values; here we only
// need to assemble the payload + show what's been touched.
//
// Scope per the 2026-05-22 design call: damper_code + 4 clicks only.
// Springs / ARB / 3rd-elements / MR are deferred until their provenance
// hardens; do NOT add controls for them.
import { useEffect, useRef, useState, type ChangeEvent } from "react";
import uPlot from "uplot";
import "uplot/dist/uPlot.min.css";

import { createRun } from "../api/tests";
import type { DamperCornerEdit, DamperSettingPayload } from "../api/tests";
import {
  fetchPartsCatalogDampers,
  damperCodesForCorner,
  isCatalogCode,
  type DampersByCorner,
} from "../api/parts_catalog";
import {
  fetchDamperCurve,
  type DamperCurve,
  type DamperCurveResponse,
} from "../api/damperCurve";

const CORNERS: readonly ("lf" | "rf" | "lr" | "rr")[] = ["lf", "rf", "lr", "rr"];
const CORNER_LABEL: Record<string, string> = {
  lf: "LF (left-front)",
  rf: "RF (right-front)",
  lr: "LR (left-rear)",
  rr: "RR (right-rear)",
};

// One corner's transient form state. Empty string means "untouched";
// at submit time empty strings drop out of the overlay (the corner
// inherits from baseline).
type CornerFormState = {
  damper_code: string;
  lsc: string;
  hsc: string;
  lsr: string;
  hsr: string;
};

const EMPTY_CORNER: CornerFormState = {
  damper_code: "", lsc: "", hsc: "", lsr: "", hsr: "",
};

function _buildInitialState(
  initialOverlay: DamperSettingPayload | null | undefined,
): Record<string, CornerFormState> {
  /* Seed form state from a stored sparse overlay (the source Run's
     damper_setting). Only fields the overlay explicitly carries become
     non-empty form values. Inherited baseline values stay BLANK in the
     editor so submitting them doesn't accidentally promote "inherit"
     into "explicit override" on the duplicated Run. */
  const state: Record<string, CornerFormState> = {
    lf: { ...EMPTY_CORNER }, rf: { ...EMPTY_CORNER },
    lr: { ...EMPTY_CORNER }, rr: { ...EMPTY_CORNER },
  };
  if (!initialOverlay) return state;
  const corners = initialOverlay.corners ?? {};
  for (const c of CORNERS) {
    const edit = corners[c];
    if (!edit) continue;
    if (typeof edit.damper_code === "string") {
      state[c].damper_code = edit.damper_code;
    }
    if (edit.clicks && edit.clicks.length === 4) {
      state[c].lsc = String(edit.clicks[0]);
      state[c].hsc = String(edit.clicks[1]);
      state[c].lsr = String(edit.clicks[2]);
      state[c].hsr = String(edit.clicks[3]);
    }
  }
  return state;
}

function _clickState(s: CornerFormState): {
  allEmpty: boolean;
  allValid: boolean;
  values: [number, number, number, number] | null;
} {
  const strings = [s.lsc, s.hsc, s.lsr, s.hsr];
  const trimmed = strings.map((x) => x.trim());
  const allEmpty = trimmed.every((x) => x.length === 0);
  if (allEmpty) {
    return { allEmpty: true, allValid: true, values: null };
  }
  if (trimmed.some((x) => x.length === 0)) {
    return { allEmpty: false, allValid: false, values: null };
  }
  const nums = trimmed.map((x) => parseInt(x, 10));
  if (nums.some((n) => !Number.isFinite(n) || n < 0 || n > 50)) {
    return { allEmpty: false, allValid: false, values: null };
  }
  return {
    allEmpty: false, allValid: true,
    values: [nums[0], nums[1], nums[2], nums[3]],
  };
}

function buildOverlay(
  state: Record<string, CornerFormState>,
): DamperSettingPayload {
  /* Build a sparse overlay. Each corner's clicks must be either ALL
     four blank (no edit) or ALL four valid integers in [0, 50] — a
     partial click set is treated as a user error and rejected by the
     submit guard above; buildOverlay just drops it. */
  const corners: DamperSettingPayload["corners"] = {};
  for (const c of CORNERS) {
    const s = state[c];
    const edit: DamperCornerEdit = {};
    const code = s.damper_code.trim();
    if (code.length > 0) edit.damper_code = code;
    const clicks = _clickState(s);
    if (clicks.allValid && clicks.values !== null) {
      edit.clicks = clicks.values;
    }
    if (Object.keys(edit).length > 0) {
      corners[c] = edit;
    }
  }
  return { corners };
}

function _cornerHasPartialClicks(s: CornerFormState): boolean {
  const cs = _clickState(s);
  return !cs.allEmpty && !cs.allValid;
}

// ---- F-V curve preview -------------------------------------------------
//
// When a corner carries a damper_code + all 4 clicks, debounce-fetch the exact
// bench curve that code+clicks resolves to (GET /api/damper_curve) and draw it
// so the engineer sees the candidate curve before submitting. Curves arrive in
// SI (m/s, N); the F-V dyno plot convention is lbf vs in/s, so we convert for
// display and label the axes explicitly.
const PREVIEW_DEBOUNCE_MS = 400;
const MPS_TO_INPS = 39.37007874015748; // 1 m/s = 39.37 in/s
const N_TO_LBF = 0.2248089430997105; // 1 N = 0.2248 lbf (= 1 / 4.4482216152605)

function _toDisplay(curve: DamperCurve): { x: number[]; y: number[] } {
  return {
    x: curve.velocity_mps.map((v) => v * MPS_TO_INPS),
    y: curve.force_N.map((f) => f * N_TO_LBF),
  };
}

// Linear interp of (srcX, srcY) onto xs; null outside the source range so uPlot
// draws a gap rather than extrapolating. srcX is monotonic ascending.
function _interpOnto(
  xs: number[], srcX: number[], srcY: number[],
): (number | null)[] {
  return xs.map((x) => {
    const n = srcX.length;
    if (n === 0 || x < srcX[0] || x > srcX[n - 1]) return null;
    let i = 1;
    while (i < n && srcX[i] < x) i++;
    const x0 = srcX[i - 1], x1 = srcX[i], y0 = srcY[i - 1], y1 = srcY[i];
    if (x1 === x0) return y0;
    return y0 + ((x - x0) / (x1 - x0)) * (y1 - y0);
  });
}

// Candidate + baseline curves ride different velocity grids, so put both on the
// union grid (uPlot is single-x). Baseline is an all-null series when absent so
// the chart's series count stays fixed.
function _buildAligned(resp: DamperCurveResponse): uPlot.AlignedData {
  const cand = resp.curve ? _toDisplay(resp.curve) : { x: [], y: [] };
  const base = resp.baseline ? _toDisplay(resp.baseline) : null;
  const xs = Array.from(new Set([...cand.x, ...(base ? base.x : [])]))
    .sort((a, b) => a - b);
  const candY = _interpOnto(xs, cand.x, cand.y);
  const baseY = base ? _interpOnto(xs, base.x, base.y) : xs.map(() => null);
  return [xs, candY, baseY] as uPlot.AlignedData;
}

function _fvOptions(): uPlot.Options {
  const axis = { stroke: "#9a9a9a", grid: { stroke: "#2a2a2a", dash: [2, 2] } };
  return {
    width: 360,
    height: 160,
    scales: { x: { time: false } },
    legend: { show: false },
    cursor: { show: false },
    series: [
      {},
      { label: "candidate", stroke: "#5aa9e6", width: 1.5 },
      { label: "baseline", stroke: "#9a9a9a", width: 1, dash: [4, 4] },
    ],
    axes: [
      { ...axis, label: "velocity (in/s)" },
      { ...axis, label: "force (lbf)" },
    ],
  };
}

function _captionText(
  status: "loading" | "error" | "ready",
  resp: DamperCurveResponse | null,
  errMsg: string | null,
): string {
  if (status === "loading") return "resolving bench curve…";
  if (status === "error") return `preview unavailable: ${errMsg ?? "request failed"}`;
  if (!resp) return "";
  if (!resp.available) return resp.reason ?? "no matching bench curve";
  const m = resp.match;
  const base = resp.baseline ? " · baseline dashed" : "";
  if (!m) return `bench curve${base}`;
  if (m.confidence === "exact") return `exact curve${base}`;
  if (m.confidence === "nearest") {
    const cl = m.matched_clicks ? ` [${m.matched_clicks.join(", ")}]` : "";
    return `nearest curve${cl}${base}`;
  }
  if (m.confidence === "code_only") {
    return `nearest curve (code only, clicks ignored)${base}`;
  }
  return `bench curve (${m.confidence})${base}`;
}

// Mount-once uPlot + setData on data change (the OverlayChart pattern, at small
// scale). uPlot needs a real canvas 2D context, absent under jsdom, so the
// construction is guarded: a chart that can't mount leaves the caption intact.
function FvChart({ data }: { data: uPlot.AlignedData }) {
  const hostRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<uPlot | null>(null);
  const dataRef = useRef(data);
  useEffect(() => { dataRef.current = data; }, [data]);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    try {
      chartRef.current = new uPlot(_fvOptions(), dataRef.current, host);
    } catch {
      chartRef.current = null;
    }
    return () => { chartRef.current?.destroy(); chartRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const u = chartRef.current;
    if (!u) return;
    try { u.setData(data); } catch { /* inert without a live chart */ }
  }, [data]);

  return <div ref={hostRef} data-testid="curve-canvas" />;
}

function DamperCurvePreview({
  testId, corner, damperCode, clicks,
}: {
  testId: number;
  corner: "lf" | "rf" | "lr" | "rr";
  damperCode: string;
  clicks: [number, number, number, number];
}) {
  const [resp, setResp] = useState<DamperCurveResponse | null>(null);
  const [status, setStatus] = useState<"loading" | "error" | "ready">("loading");
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const clicksKey = clicks.join(",");

  useEffect(() => {
    const controller = new AbortController();
    setStatus("loading");
    const handle = setTimeout(() => {
      fetchDamperCurve(
        { testId, corner, damperCode, clicks },
        { signal: controller.signal },
      )
        .then((r) => { setResp(r); setStatus("ready"); })
        .catch((e: unknown) => {
          if (e instanceof DOMException && e.name === "AbortError") return;
          setErrMsg(e instanceof Error ? e.message : String(e));
          setStatus("error");
        });
    }, PREVIEW_DEBOUNCE_MS);
    return () => { clearTimeout(handle); controller.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testId, corner, damperCode, clicksKey]);

  const showChart =
    status === "ready" && resp?.available === true && resp.curve != null;
  return (
    <div
      data-testid={`curve-preview-${corner}`}
      style={{ marginTop: "8px", borderTop: "1px solid #2a2a2a", paddingTop: "6px" }}
    >
      {showChart && resp && <FvChart data={_buildAligned(resp)} />}
      <p
        className="muted"
        data-testid={`curve-caption-${corner}`}
        style={{ fontSize: "11px", margin: "4px 0 0" }}
      >
        {_captionText(status, resp, errMsg)}
      </p>
    </div>
  );
}

export type DamperEditSheetProps = {
  testId: number;
  baselineRunId: number | null;
  // Optional pre-fill from a source Run's stored sparse overlay
  // (duplicate-and-edit workflow). null/undefined → blank editor.
  initialOverlay?: DamperSettingPayload | null;
  // Optional header label so the engineer sees which Run they're
  // duplicating from. Falls back to a generic title when omitted.
  sourceRunLabel?: string | null;
  onClose: () => void;
  onRunCreated: () => void;
};

export function DamperEditSheet({
  testId, baselineRunId, initialOverlay, sourceRunLabel,
  onClose, onRunCreated,
}: DamperEditSheetProps) {
  const [state, setState] = useState<Record<string, CornerFormState>>(
    () => _buildInitialState(initialOverlay),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [catalog, setCatalog] = useState<DampersByCorner | null>(null);

  useEffect(() => {
    // One-shot catalog fetch on mount. A failure leaves catalog=null,
    // which collapses the picker to a plain text input (off-catalog
    // path) so engineers can still create Runs if the catalog endpoint
    // is degraded.
    let cancelled = false;
    fetchPartsCatalogDampers()
      .then((c) => { if (!cancelled) setCatalog(c); })
      .catch(() => { /* silent — picker degrades to text-only */ });
    return () => { cancelled = true; };
  }, []);

  const update = (
    corner: string, field: keyof CornerFormState,
  ) => (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setState((s) => ({ ...s, [corner]: { ...s[corner], [field]: value } }));
  };

  const overlay = buildOverlay(state);
  const editedCorners = Object.keys(overlay.corners ?? {});
  const partialClickCorners = CORNERS.filter(
    (c) => _cornerHasPartialClicks(state[c]),
  );
  const hasPartialClicks = partialClickCorners.length > 0;

  const submit = async () => {
    if (hasPartialClicks) {
      setError(
        `Incomplete click sets on ${partialClickCorners.join(", ")} — `
        + "fill all 4 clicks (LSC/HSC/LSR/HSR) or leave the corner blank.",
      );
      return;
    }
    if (editedCorners.length === 0) {
      setError("No edits — touch at least one corner before saving.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await createRun(testId, {
        baseline_run_id: baselineRunId,
        damper_setting: overlay,
      });
      onRunCreated();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-label="Damper edit"
      data-testid="damper-edit-sheet"
      style={{
        position: "fixed", top: 0, right: 0, bottom: 0,
        width: "min(640px, 100vw)", overflowY: "auto",
        backgroundColor: "#1a1a1a", padding: "16px",
        boxShadow: "-4px 0 16px rgba(0,0,0,0.4)",
        zIndex: 50,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "16px" }}>
        <h3 style={{ margin: 0 }}>
          {sourceRunLabel
            ? `Edit as new Run · duplicating ${sourceRunLabel}`
            : "Edit damper setting"}
        </h3>
        <button type="button" className="btn" onClick={onClose} data-testid="edit-close">
          Close
        </button>
      </div>
      <p className="muted" style={{ fontSize: "12px" }}>
        Sparse overlay — only edited fields go in the payload; untouched
        corners inherit from the Test's baseline XLSM. Scope: damper code
        + 4 clicks (LSC / HSC / LSR / HSR) per corner.
      </p>

      {CORNERS.map((c) => {
        const cs = _clickState(state[c]);
        const partial = !cs.allEmpty && !cs.allValid;
        const cornerCodes = catalog ? damperCodesForCorner(catalog, c) : [];
        const listId = `damper-code-options-${c}`;
        const typed = state[c].damper_code.trim();
        // Preview fires only once a corner is fully specified: a code plus all
        // four valid clicks. Partial clicks never trigger a fetch.
        const showPreview = typed.length > 0 && cs.allValid && cs.values !== null;
        const offCatalog =
          catalog !== null
          && cornerCodes.length > 0
          && typed.length > 0
          && !isCatalogCode(typed, cornerCodes);
        return (
          <fieldset key={c} style={{ marginBottom: "12px", padding: "8px", border: partial ? "1px solid #ff9a9a" : "1px solid #333" }}>
            <legend style={{ padding: "0 4px", fontSize: "12px" }}>{CORNER_LABEL[c]}</legend>
            <label style={{ display: "block", marginBottom: "4px", fontSize: "12px" }}>
              damper_code
              <input
                type="text"
                className="input"
                data-testid={`edit-${c}-code`}
                value={state[c].damper_code}
                onChange={update(c, "damper_code")}
                placeholder="e.g. JHRC202 180K"
                list={catalog && cornerCodes.length > 0 ? listId : undefined}
                style={{ width: "100%", marginTop: "2px" }}
              />
              {catalog && cornerCodes.length > 0 && (
                <datalist id={listId} data-testid={`edit-${c}-code-options`}>
                  {cornerCodes.map((code) => (
                    <option key={code} value={code} />
                  ))}
                </datalist>
              )}
            </label>
            {offCatalog && (
              <p
                data-testid={`edit-${c}-off-catalog`}
                style={{ color: "#e0c97a", fontSize: "11px", margin: "0 0 4px" }}
              >
                Not in the catalog for this corner. Save may rely on
                parser / PVP fallback if the code is valid.
              </p>
            )}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: "4px" }}>
              {(["lsc", "hsc", "lsr", "hsr"] as const).map((field) => (
                <label key={field} style={{ fontSize: "12px" }}>
                  {field.toUpperCase()}
                  <input
                    type="number"
                    className="input"
                    min={0}
                    max={50}
                    data-testid={`edit-${c}-${field}`}
                    value={state[c][field]}
                    onChange={update(c, field)}
                    style={{ width: "100%", marginTop: "2px" }}
                  />
                </label>
              ))}
            </div>
            {partial && (
              <p
                data-testid={`edit-${c}-click-warning`}
                style={{ color: "#ff9a9a", fontSize: "11px", margin: "4px 0 0" }}
              >
                Fill all 4 clicks (LSC/HSC/LSR/HSR) or leave them all blank.
                A partial click set would silently drop on submit.
              </p>
            )}
            {showPreview && cs.values && (
              <DamperCurvePreview
                testId={testId}
                corner={c}
                damperCode={typed}
                clicks={cs.values}
              />
            )}
          </fieldset>
        );
      })}

      <p className="muted" style={{ fontSize: "11px", marginTop: "8px" }}>
        {editedCorners.length === 0
          ? "No edits yet."
          : `Will overlay corners: ${editedCorners.join(", ")}.`}
      </p>

      {error && (
        <p
          data-testid="edit-error"
          style={{ color: "#ff9a9a", fontSize: "12px", marginTop: "8px" }}
        >
          {error}
        </p>
      )}

      <div style={{ display: "flex", gap: "8px", marginTop: "16px" }}>
        <button
          type="button"
          className="btn primary"
          onClick={submit}
          disabled={busy || editedCorners.length === 0 || hasPartialClicks}
          data-testid="edit-submit"
        >
          {busy ? "Enqueuing…" : "Save & Run"}
        </button>
        <button type="button" className="btn" onClick={onClose}>
          Cancel
        </button>
      </div>
    </div>
  );
}
